import { useEffect, useState } from 'react';

import { useSubscriptionDowngraded } from '@/features/teamspace';

import { getEnterpriseSubscriptionState, useBalanceStore, useUserBalanceStore } from '@/entities/balance';
import { BalanceStoreActions } from '@/entities/balance/lib/types';
import { useProducts } from '@/entities/pricing';
import { useGetSpaceCredits } from '@/entities/teamspace';
import { useCurrentUser } from '@/entities/user/currentUser';

import { setMixpanelSubscriptionProfile, useMixpanelPropsStore } from '@/shared/thirdParty/mixpanel';
import { SentryRask } from '@/shared/thirdParty/sentry';
import { Products, Subscription, SubscriptionStatus } from '@/shared/types/Billing';

const useUpdateBalance = (
  products?: Products,
  subscription?: null | Subscription,
  update?: BalanceStoreActions['updateBalance'],
) => {
  const isSubscriptionDowngraded = useSubscriptionDowngraded();
  const planId = subscription?.plan.product_id;
  const priceId = subscription?.plan.id;
  const subscriptionStatus = subscription?.status;

  useEffect(() => {
    if (products) {
      const enterpriseSubscriptionState = getEnterpriseSubscriptionState(products.plan, planId, subscriptionStatus);
      const canBuyMinutes = products.credit.length > 0 && products.credit[0].prices.length > 0;
      const userPlan = products.plan.find((plan) => plan.id === planId);

      const hasSubscriptionWithArchivedPrice = userPlan
        ? userPlan.prices.find((price) => price.id === priceId)?.status === 'archived'
        : false;

      const balanceUpdate = {
        ...enterpriseSubscriptionState,
        canBuyMinutes,
        hasArchivedSubscription: Boolean(userPlan?.is_archived),
        hasCanceledSubscription: subscriptionStatus === SubscriptionStatus.Canceled,
        hasSubscriptionWithArchivedPrice,
        isSubscriptionDowngraded,
        planCategory: userPlan?.category,
        planName: userPlan?.name,
      };

      update?.(balanceUpdate);
    }
  }, [products, planId, subscriptionStatus, priceId, update, isSubscriptionDowngraded]);
};

export const useBalance = (currentSpaceId: string) => {
  const { data: user } = useCurrentUser();
  const { products } = useProducts(true, { showErrors: false });
  const [balanceCatalog, setBalanceCatalog] = useState({ products, userId: user?.id });
  const balanceProducts = products ?? (balanceCatalog.userId === user?.id ? balanceCatalog.products : undefined);

  // Recompute the current plan's metadata while its fresh pricing catalog loads.
  useEffect(() => {
    setBalanceCatalog((current) =>
      products || current.userId !== user?.id ? { products, userId: user?.id } : current,
    );
  }, [products, user?.id]);

  const credits = useGetSpaceCredits(currentSpaceId);

  const setBalance = useBalanceStore((state) => state.setBalance);
  const updateBalance = useBalanceStore((state) => state.updateBalance);
  const setUserBalance = useUserBalanceStore((state) => state.setBalance);
  const updateUserBalance = useUserBalanceStore((state) => state.updateBalance);
  const setMixpanelSubscriptionData = useMixpanelPropsStore((state) => state.setMixpanelSubscriptionData);
  const subScriptionName = useBalanceStore((state) => state.planName);
  const hasActiveSubscription = useBalanceStore((state) => state.hasActiveSubscription);
  const hasFreeSubscription = useBalanceStore((state) => state.hasFreeSubscription);
  const hasTrialPlan = useBalanceStore((state) => state.hasTrialPlan);
  const { interval, product_id: productId } = user?.subscription?.plan || {};

  useUpdateBalance(balanceProducts, user?.subscription, updateBalance);
  useUpdateBalance(
    balanceProducts,
    user?.is_superuser ? user?.subscription : credits.data?.subscription,
    updateUserBalance,
  );

  useEffect(() => {
    if (user) {
      setUserBalance(user);
    }
  }, [user, setUserBalance]);

  useEffect(() => {
    if (!user) return;

    if (credits.data && !user.is_superuser) {
      return setBalance(credits.data);
    }

    setBalance(user);
  }, [credits.data, setBalance, user]);

  useEffect(() => {
    if (subScriptionName) {
      SentryRask.setTag('plan', subScriptionName);
    }
  }, [subScriptionName]);

  useEffect(() => {
    setMixpanelSubscriptionData({
      hasActiveSubscription,
      hasFreeSubscription,
      hasTrialPlan,
      interval,
      productId,
      products,
    });

    setMixpanelSubscriptionProfile({
      hasActiveSubscription,
      hasTrialPlan,
      planInterval: interval,
      planName: subScriptionName ?? undefined,
    });
  }, [
    hasActiveSubscription,
    hasFreeSubscription,
    hasTrialPlan,
    interval,
    productId,
    products,
    setMixpanelSubscriptionData,
    subScriptionName,
  ]);
};
